import { useTranslation } from "@repo/i18n";
import { Button, Skeleton, Textarea } from "@repo/components";
import { css } from "@repo/styled-system/css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ChangeEvent, useState } from "react";
import { Link } from "react-router";
import { useVenue } from "../../shell/VenueContext";
import { useFeatureAvailability } from "../../shell/useFeatureAvailability";
import {
  guestsApi,
  guestsKeys,
  type GuestListItem,
  type GuestNote,
  type GuestProfile,
} from "./guestsApi";

const badgeBase = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "1",
  px: "2",
  py: "0.5",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "2xs",
  fontWeight: "medium",
  whiteSpace: "nowrap",
});
const tagBadge = css({ bg: "bg.default", color: "fg.muted", borderColor: "border.default" });
const flagBadge = css({
  bg: "booking.noShow.bg",
  color: "booking.noShow.fg",
  borderColor: "booking.noShow.border",
});

function formatWhen(iso: string, lang: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(lang, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone,
  }).format(d);
}

/**
 * The client detail rendered INLINE in an expanded directory row (spanning the columns) — visit counters,
 * tags, and the append-only NOTES timeline with an add-note box. Notes are staff-only (GU-4). Adding a note
 * needs the CRM_NOTES_TAGS entitlement (the server 403s otherwise); when the plan lacks it the timeline
 * stays readable and the add box is replaced by an upgrade prompt.
 */
export function GuestDetailPanel({ venueId, guest }: { venueId: string; guest: GuestListItem }) {
  const { t, i18n } = useTranslation();
  const { venue } = useVenue();
  const qc = useQueryClient();
  const [body, setBody] = useState("");

  const profile = useQuery({
    queryKey: guestsKeys.profile(venueId, guest.guestId),
    queryFn: () => guestsApi.profile(venueId, guest.guestId),
  });

  const canAddNote = useFeatureAvailability({ feature: "CRM_NOTES_TAGS" }).status === "available";

  const addNote = useMutation({
    mutationFn: () => guestsApi.addNote(venueId, guest.guestId, body.trim()),
    onSuccess: (updated: GuestProfile) => {
      qc.setQueryData(guestsKeys.profile(venueId, guest.guestId), updated);
      setBody("");
    },
  });

  const notes = (profile.data?.notes ?? []).toSorted((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );

  return (
    <div
      aria-label={t("guests.detail.title")}
      data-testid="guest-detail"
      className={css({
        px: { base: "4", md: "6" },
        py: "4",
        display: "flex",
        flexDirection: "column",
        gap: "3",
        maxW: "2xl",
        minW: "0",
      })}
    >
      <div className={css({ fontSize: "sm", color: "fg.default", fontWeight: "medium" })}>
        {guest.phone ?? guest.email ?? "—"}
      </div>
      <div className={css({ fontSize: "sm", color: "fg.muted" })}>
        {t("guests.directory.visitsSummary", {
          completed: guest.completed,
          noShows: guest.noShows,
          cancellations: guest.guestCancellations,
        })}
      </div>
      {guest.tags.length > 0 && (
        <div className={css({ display: "flex", gap: "1", flexWrap: "wrap" })}>
          {guest.tags.map((tag) => (
            <span key={tag} className={`${badgeBase} ${tagBadge}`}>
              {tag}
            </span>
          ))}
        </div>
      )}

      <div
        className={css({ borderTopWidth: "1px", borderColor: "border.subtle", pt: "3", mt: "1" })}
      >
        <h3 className={css({ fontSize: "sm", fontWeight: "semibold", mb: "2" })}>
          {t("guests.notes.title")}
        </h3>

        {profile.isPending && <Skeleton height="12" />}
        {profile.isError && (
          <p role="alert" className={css({ fontSize: "sm", color: "fg.error" })}>
            {t("guests.directory.loadError")}
          </p>
        )}
        {profile.data &&
          (notes.length === 0 ? (
            <p
              className={css({ fontSize: "sm", color: "fg.muted" })}
              data-testid="guest-notes-empty"
            >
              {t("guests.notes.empty")}
            </p>
          ) : (
            <ul
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "2",
                listStyle: "none",
                p: "0",
                m: "0",
              })}
              data-testid="guest-notes"
            >
              {notes.map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  lang={i18n.language}
                  timeZone={venue.timeZone}
                  t={t}
                />
              ))}
            </ul>
          ))}

        {canAddNote ? (
          <form
            className={css({ display: "flex", flexDirection: "column", gap: "2", mt: "3" })}
            onSubmit={(e) => {
              e.preventDefault();
              if (body.trim()) addNote.mutate();
            }}
          >
            <Textarea
              value={body}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setBody(e.target.value)}
              placeholder={t("guests.notes.placeholder")}
              rows={3}
              data-testid="guest-note-input"
            />
            {addNote.isError && (
              <span role="alert" className={css({ fontSize: "xs", color: "fg.error" })}>
                {t("guests.notes.addError")}
              </span>
            )}
            <Button
              type="submit"
              size="sm"
              alignSelf="flex-start"
              data-testid="guest-note-submit"
              loading={addNote.isPending}
              disabled={!body.trim()}
            >
              {t("guests.notes.add")}
            </Button>
          </form>
        ) : (
          <p
            className={css({ fontSize: "sm", color: "fg.muted", mt: "3" })}
            data-testid="guest-notes-upgrade"
          >
            {t("guests.notes.upgrade")}{" "}
            <Link
              to={`/v/${venueId}/billing`}
              className={css({
                color: "colorPalette.text",
                textDecoration: "underline",
                fontWeight: "medium",
              })}
            >
              {t("entitlement.upgrade.cta")}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}

function NoteRow({
  note,
  lang,
  timeZone,
  t,
}: {
  note: GuestNote;
  lang: string;
  timeZone: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <li className={css({ fontSize: "sm" })}>
      <div className={css({ display: "flex", gap: "2", alignItems: "baseline", flexWrap: "wrap" })}>
        {note.kind === "QUICK_FLAG" && (
          <span className={`${badgeBase} ${flagBadge}`}>{t("guests.notes.flag")}</span>
        )}
        <span className={css({ color: "fg.muted", fontSize: "xs" })}>
          {formatWhen(note.createdAt, lang, timeZone)}
        </span>
      </div>
      <div className={css({ whiteSpace: "pre-wrap" })}>{note.body}</div>
    </li>
  );
}
